'use client';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

interface SliderProps {
  children: React.ReactNode;
  className?: string;
  showDots?: boolean;
  showArrows?: boolean;
  infinite?: boolean;
  slideWidth?: number;
  maxSlideWidthPx?: number;
  autoPlay?: boolean;
  autoPlayInterval?: number;
  ariaLabel?: string;
}

const TRANSITION_MS = 500;

const Slider: React.FC<SliderProps> = ({
  children,
  className = '',
  showDots = false,
  showArrows = true,
  infinite = true,
  slideWidth: slideWidthProp,
  maxSlideWidthPx,
  autoPlay = false,
  autoPlayInterval = 5000,
  ariaLabel = 'Carousel',
}) => {
  const childrenArray = React.Children.toArray(children);
  const totalSlides = childrenArray.length;
  const isLooping = infinite && totalSlides > 1;

  // A fixed peek percentage (e.g. 80%) that looks right on desktop leaves an
  // overly cramped sliver on phones, since the same percentage of a much
  // narrower viewport is a much narrower peek. When a caller doesn't pin an
  // explicit slideWidth, scale it down responsively so any slider — current
  // or future — gets a sensible peek at every breakpoint with no call-site
  // changes required.
  const [autoSlideWidth, setAutoSlideWidth] = useState(80);
  useEffect(() => {
    if (slideWidthProp !== undefined) return;
    const update = () => {
      if (window.innerWidth < 640) setAutoSlideWidth(82);
      else if (window.innerWidth < 1024) setAutoSlideWidth(86);
      else setAutoSlideWidth(80);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [slideWidthProp]);
  const slideWidth = slideWidthProp ?? autoSlideWidth;

  // When looping, the track is three full copies of the slides back to back:
  // [...originals, ...originals, ...originals]. currentIndex starts in the
  // middle copy so there's always a full extra copy to scroll into on either
  // side, however many slides are visible at once (a single peeking sliver
  // or several full cards per view).
  const displaySlides = isLooping ? [...childrenArray, ...childrenArray, ...childrenArray] : childrenArray;

  const [currentIndex, setCurrentIndex] = useState(isLooping ? totalSlides : 0);
  const [isAnimating, setIsAnimating] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const viewportRef = useRef<HTMLDivElement | null>(null);

  // The track is positioned by the *actual rendered offset* of the target
  // slide (measured via offsetLeft), not an assumed `currentIndex *
  // slideWidth%`. Any spacing a caller's card markup adds between slides
  // (gap, padding, margin, anything) lives outside that percentage math and
  // would silently drift the two out of sync after enough steps. Measuring
  // the real DOM position keeps the slider correct no matter what layout a
  // given set of slides uses — no changes to this component are needed to
  // support a future slider with different spacing.
  //
  // The slide is centered in the viewport rather than pinned to its left
  // edge, so any spare width (the "peek") splits evenly left/right instead
  // of landing entirely on one side. When slideWidth divides evenly into
  // 100% (e.g. multi-up grids showing exactly 2 or 3 per view) the centering
  // offset works out to a whole slide-width, so it has no visible effect.
  const [offsetPx, setOffsetPx] = useState(0);

  useLayoutEffect(() => {
    const measure = () => {
      const el = slideRefs.current[currentIndex];
      const viewport = viewportRef.current;
      if (el && viewport) {
        setOffsetPx(el.offsetLeft - (viewport.clientWidth - el.offsetWidth) / 2);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [currentIndex, slideWidth, totalSlides]);

  // Touch/Swipe state (raw pixels — matches the pixel-based track offset)
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffsetPx, setDragOffsetPx] = useState(0);

  const minSwipeDistance = 50;

  // Respect the OS-level "reduce motion" setting (WCAG 2.3.3): users with
  // vestibular sensitivity shouldn't be forced through the slide animation.
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mql.matches);
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const handleNext = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setIsAnimating(true);
    if (isLooping) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      setCurrentIndex((prev) => Math.min(prev + 1, totalSlides - 1));
    }
  };

  const handlePrev = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setIsAnimating(true);
    if (isLooping) {
      setCurrentIndex((prev) => prev - 1);
    } else {
      setCurrentIndex((prev) => Math.max(prev - 1, 0));
    }
  };

  const goToSlide = (index: number) => {
    if (isTransitioning || isDragging) return;
    setIsTransitioning(true);
    setIsAnimating(true);
    setCurrentIndex(isLooping ? totalSlides + index : index);
  };

  useEffect(() => {
    if (autoPlay && totalSlides > 1 && !isDragging && !isTransitioning) {
      const interval = setInterval(handleNext, autoPlayInterval);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlay, autoPlayInterval, totalSlides, isDragging, isTransitioning]);

  // Settle the transition: unlock interaction, and if we've drifted into the
  // leading or trailing copy, silently snap back into the middle copy with
  // no animation (same content, so the snap is invisible).
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isLooping && currentIndex >= totalSlides * 2) {
        setIsAnimating(false);
        setCurrentIndex((prev) => prev - totalSlides);
      } else if (isLooping && currentIndex < totalSlides) {
        setIsAnimating(false);
        setCurrentIndex((prev) => prev + totalSlides);
      }
      setIsTransitioning(false);
    }, TRANSITION_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  // Re-enable the transition on the next frame after a silent snap.
  useEffect(() => {
    if (!isAnimating) {
      const id = requestAnimationFrame(() => setIsAnimating(true));
      return () => cancelAnimationFrame(id);
    }
  }, [isAnimating]);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart || isTransitioning) return;
    const currentTouch = e.targetTouches[0].clientX;
    setDragOffsetPx(touchStart - currentTouch);
    setTouchEnd(currentTouch);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    if (!touchStart || !touchEnd || isTransitioning) {
      setDragOffsetPx(0);
      return;
    }
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) {
      if (isLooping || currentIndex < totalSlides - 1) handleNext();
    } else if (isRightSwipe) {
      if (isLooping || currentIndex > 0) handlePrev();
    }
    setTouchStart(null);
    setTouchEnd(null);
    setDragOffsetPx(0);
  };

  const getTransform = () => {
    const dragTransform = isDragging ? -(dragOffsetPx * 0.8) : 0;
    return -offsetPx + dragTransform;
  };

  const activeDotIndex = isLooping ? ((currentIndex % totalSlides) + totalSlides) % totalSlides : currentIndex;

  return (
    <div
      className="relative w-full max-w-[1440px] h-full mx-auto"
      role="region"
      aria-roledescription="carousel"
      aria-label={ariaLabel}
    >
      {/* Announces the active slide to screen readers; visually hidden */}
      <div className="sr-only" aria-live="polite">
        Slide {activeDotIndex + 1} of {totalSlides}
      </div>

      <div className="flex items-stretch gap-2 sm:gap-5">
        {showArrows && totalSlides > 1 && (
          <button
            onClick={handlePrev}
            disabled={!isLooping && currentIndex === 0}
            className="hidden sm:block flex-shrink-0 self-center bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-1.5 sm:p-3 shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Previous slide"
          >
            <svg className="w-3.5 h-3.5 sm:w-6 sm:h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        <div className="flex-1 overflow-hidden" ref={viewportRef}>
          <div
            className={`flex items-stretch relative gap-2 sm:gap-4 ${className}`}
            style={{
              transform: `translateX(${getTransform()}px)`,
              width: '100%',
              transition: isAnimating && !isDragging && !reducedMotion ? `transform ${TRANSITION_MS}ms ease-in-out` : 'none',
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {displaySlides.map((child, index) => {
              // Only the middle copy is the "real", canonical set of slides;
              // the leading/trailing clones exist purely so the loop has
              // something to scroll into and must stay invisible to
              // assistive tech and keyboard nav, or every link/button inside
              // every slide would be reachable three times.
              const isClone = isLooping && (index < totalSlides || index >= totalSlides * 2);
              return (
                <div
                  key={index}
                  ref={(el) => {
                    slideRefs.current[index] = el;
                  }}
                  style={{ width: `${slideWidth}%`, maxWidth: maxSlideWidthPx !== undefined ? `${maxSlideWidthPx}px` : undefined }}
                  className="flex-shrink-0 flex-grow self-stretch overflow-hidden"
                  aria-hidden={isClone}
                  inert={isClone}
                >
                  {child}
                </div>
              );
            })}
          </div>
        </div>

        {showArrows && totalSlides > 1 && (
          <button
            onClick={handleNext}
            disabled={!isLooping && currentIndex === totalSlides - 1}
            className="hidden sm:block flex-shrink-0 self-center bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-1.5 sm:p-3 shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Next slide"
          >
            <svg className="w-3.5 h-3.5 sm:w-6 sm:h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>

      {/* Dots Indicator */}
      {showDots && totalSlides > 1 && (
        <div className="w-full mt-[1.875rem] flex justify-center space-x-2">
          {childrenArray.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-2 sm:h-3 rounded-md transition-all duration-200 ${
                index === activeDotIndex
                  ? 'w-12 sm:w-16 bg-[#1ca7e5] '
                  : ' w-3 sm:w-4 bg-[#1ca7e5] bg-opacity-50 hover:bg-opacity-75'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Slider;
